import FormattedMessage from 'I18n/components/FormattedMessage';
import FullContainerCardFormatter from 'crm-cards-sdk/card-formatters/FullContainerCardFormatter';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Tabs, Tab } from 'foundations-components/transitional/components/Tabs';
import { CardContainerContextProvider } from 'crm-cards-sdk/internal/CardContainerContext';
import { useDispatch as usePerfStoreDispatch } from 'crm-cards-sdk/internal/perf/LocationStateContext';
import { useRestoreableLocationState } from '../internal/restoreableLocationState/hooks/useRestoreableLocationState';
import { useSendCrmMessageTopic } from 'crm-message-bus/useSendCrmMessageTopic';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { getIsTimelineContainer } from '../content-locators/findTimelineContainer';
import { createContainerContentKey } from './createContainerContentKey';
import { AIBadge } from 'ai-components-ui-library/components/informative/AIBadge';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { Text } from 'foundations-components/components/Text';
import MaybeScrollingContainer from './MaybeScrollingContainer';
import { View } from 'foundations-components/components/View';
import { useIsScoped } from 'crm-cards-sdk/hooks/useIsScoped';
import TabsWithSettingsButton from './TabsWithSettingsButton';
import { useIsUngated } from 'crm-cards-sdk/hooks/useIsUngated';
import { gate } from 'hub-http/gates';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getTabDefinition(containerOrCard, idx) {
  if (containerOrCard.type === 'CONTAINER') {
    return {
      tabId: `${idx}`,
      title: containerOrCard.label,
      content: containerOrCard,
      labelBadge: containerOrCard.labelBadge
    };
  }
  return {
    tabId: `${idx}`,
    title: containerOrCard.definition.cardTitle || '',
    content: containerOrCard
  };
}
function renderTitleWithLabelBadge(title, labelBadge) {
  let badge;
  switch (labelBadge) {
    case 'AIBadge':
      badge = /*#__PURE__*/_jsx(AIBadge, {});
      break;
    case 'new':
      badge = /*#__PURE__*/_jsx(Badge, {
        use: "new",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "crmCards.labelBadge.new"
        })
      });
      break;
    case 'beta':
      badge = /*#__PURE__*/_jsx(Badge, {
        use: "beta",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "crmCards.labelBadge.beta"
        })
      });
      break;
    default:
      break;
  }
  return /*#__PURE__*/_jsxs(Text, {
    as: "span",
    children: [/*#__PURE__*/_jsx(Text, {
      as: "span",
      marginInlineEnd: "100",
      children: title
    }), badge]
  });
}
export function _findDefaultTab(tabs, tabFromRestoreableLocationState, setTabInRestoreableLocationState, focusedItem) {
  if (!tabs || tabs.length <= 0) {
    return undefined;
  }
  if (focusedItem) {
    const focusedTab = tabs.find(tab => {
      if (tab.content.type === 'CARD' && focusedItem.type === 'CARD') {
        return tab.content.definition.cardId === focusedItem.id;
      }
      if (tab.content.type === 'CONTAINER' && focusedItem.type === 'CONTAINER') {
        // Sub containers don't have ids unless explicitly set (e.g. ACTIVITIES tab)
        // So we fall back to matching on the label
        if (focusedItem.id !== null) {
          return tab.content.id === focusedItem.id;
        } else {
          return tab.title === focusedItem.label;
        }
      }
      return false;
    });
    if (focusedTab) {
      setTabInRestoreableLocationState(focusedTab.title);
      return focusedTab.tabId;
    }
  }
  const restoredTab = tabs.find(tab => tab.title === tabFromRestoreableLocationState);
  if (restoredTab) {
    return restoredTab.tabId;
  }
  return tabs[0].tabId;
}
function getTabUse(layoutUse) {
  if (layoutUse === 'scrollable') {
    return 'enclosed';
  }
  if (layoutUse && ['scrollable', 'enclosed-shaded'].includes(layoutUse)) {
    return 'enclosed-shaded';
  }
  return 'default';
}
function getShouldFill(layoutUse) {
  if (layoutUse === 'scrollable') {
    return true;
  }
  return false;
}
function getShouldScroll(layoutUse) {
  if (layoutUse === 'scrollable') {
    return true;
  }
  return false;
}
const contentStyles = {
  paddingBlockStart: '400',
  paddingBlockEnd: '400'
};
export default function TabbedContainerFormatter({
  containerId,
  contents,
  CrmContainerContentRenderer,
  focusedItem,
  layout,
  topSlot,
  observerManager,
  CardFormatter = FullContainerCardFormatter
}) {
  const tabs = useMemo(() => contents.map(getTabDefinition), [contents]);
  const isUngatedForPreserveTabContent = useIsUngated(gate('CRM:card-infra-frontend:preserve-tab-content'));
  const hasPageEditorAccess = useIsScoped('crm-page-editor-admin');
  const sendTabOpenedMessage = useSendCrmMessageTopic(TOPIC_NAMES.CONTAINER_TAB_OPENED);
  const [tabFromRestoreableLocationState, setTabInRestoreableLocationState] = useRestoreableLocationState();
  const defaultActiveTab = _findDefaultTab(tabs, tabFromRestoreableLocationState, setTabInRestoreableLocationState, focusedItem);
  const [activeTabId, setActiveTabId] = useState(defaultActiveTab);
  const [viewedTabIds, setViewedTabIds] = useState(activeTabId ? [activeTabId] : []);
  const contentByTabId = useMemo(() => {
    const contentById = {};
    tabs.forEach(tab => {
      contentById[tab.tabId] = tab.content;
    });
    return contentById;
  }, [tabs]);
  const handleTabOpened = useCallback((tabId, isOpenedAsDefault = false) => {
    const tab = tabs[+tabId];
    if (tab) {
      const isActivitiesTab = getIsTimelineContainer(tab.content);
      sendTabOpenedMessage({
        tabLabel: tab.title,
        isOpenedAsDefault,
        isActivitiesTab
      });
    }
  }, [sendTabOpenedMessage, tabs]);
  useEffect(() => {
    if (defaultActiveTab) handleTabOpened(defaultActiveTab, true);
    // dependency array intentionally left empty so this message is only fired once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const dispatch = usePerfStoreDispatch();
  const handleSelectedTabChanged = useCallback(({
    target: {
      value
    }
  }) => {
    const tabId = `${value}`;
    const tabIdNumber = parseInt(tabId, 10);
    if (!isNaN(tabIdNumber) && tabIdNumber !== undefined && tabId !== activeTabId) {
      if (!viewedTabIds.includes(tabId)) {
        setViewedTabIds(prev => [...prev, tabId]);
      }
      setActiveTabId(tabId);
      const tab = tabs[tabIdNumber];
      if (tab) {
        setTabInRestoreableLocationState(tab.title);
        handleTabOpened(tabId);
        dispatch({
          type: 'CONTAINER_TAB_CHANGED'
        });
      }
    }
  }, [activeTabId, tabs, setTabInRestoreableLocationState, handleTabOpened, dispatch, viewedTabIds]);
  const getContentStyles = useCallback(tabContent => {
    if (!tabContent || tabContent.type === 'CONTAINER' && tabContent.layout.type === 'STACK') {
      return undefined;
    }
    return contentStyles;
  }, []);
  const [tabsHeight, setTabsHeight] = useState(49);
  const myRef = useCallback(node => {
    if (node && node.offsetHeight && node.offsetHeight !== tabsHeight) {
      setTabsHeight(node.offsetHeight);
    }
  }, [tabsHeight]);
  const shouldRenderSettingsButton = layout.showSettingsButton && hasPageEditorAccess;
  const tabsElement = useMemo(() => {
    return /*#__PURE__*/_jsx(Tabs, {
      defaultSelected: defaultActiveTab,
      onSelectedChange: handleSelectedTabChanged,
      use: getTabUse(layout.use),
      fill: getShouldFill(layout.use),
      children: tabs.map(({
        tabId,
        title,
        content: tabContent,
        labelBadge
      }) => {
        return /*#__PURE__*/_jsx(Tab, {
          tabId: tabId,
          title: labelBadge ? renderTitleWithLabelBadge(title, labelBadge) : title
        }, `Tab-${tabId}:${createContainerContentKey(containerId, tabContent)}`);
      })
    });
  }, [defaultActiveTab, handleSelectedTabChanged, layout.use, tabs, containerId]);
  return /*#__PURE__*/_jsxs(CardContainerContextProvider, {
    value: {
      DefaultCardFormatter: CardFormatter,
      reorderable: false,
      observerManager
    },
    children: [/*#__PURE__*/_jsx("div", {
      ref: myRef,
      children: shouldRenderSettingsButton ? /*#__PURE__*/_jsx(TabsWithSettingsButton, {
        children: tabsElement
      }) : tabsElement
    }), isUngatedForPreserveTabContent ? viewedTabIds.map(tabId => {
      const tabContent = contentByTabId[tabId];
      return /*#__PURE__*/_jsx(View, {
        "data-test-id": `tab-${tabId}-content`,
        display: tabId !== activeTabId ? 'none' : 'block',
        blockSize: getShouldScroll(layout.use) ? `calc(100% - ${tabsHeight}px)` : undefined,
        marginBlockStart: "25",
        children: /*#__PURE__*/_jsx(MaybeScrollingContainer, {
          shouldScroll: getShouldScroll(layout.use),
          showScrollOverhangs: true,
          scrollContainerSelector: containerId || undefined,
          children: /*#__PURE__*/_jsx(CrmContainerContentRenderer, {
            contentStyles: getContentStyles(tabContent),
            content: tabContent,
            topSlot: topSlot,
            tabIndex: tabId
          })
        })
      }, `tab-${tabId}-content`);
    }) : activeTabId ? /*#__PURE__*/_jsx(View, {
      blockSize: getShouldScroll(layout.use) ? `calc(100% - ${tabsHeight}px)` : undefined,
      marginBlockStart: "25",
      children: /*#__PURE__*/_jsx(MaybeScrollingContainer, {
        shouldScroll: getShouldScroll(layout.use),
        showScrollOverhangs: true,
        scrollContainerSelector: containerId || undefined,
        children: /*#__PURE__*/_jsx(CrmContainerContentRenderer, {
          contentStyles: getContentStyles(contentByTabId[activeTabId]),
          content: contentByTabId[activeTabId],
          topSlot: topSlot,
          tabIndex: activeTabId
        })
      })
    }) : null]
  });
}